<template>
  <router-link v-if="allowed" v-slot="{ href, navigate, isActive }" :to custom>
    <!-- Click handler is triggered by keyboard as well -->
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/click-events-have-key-events -->
    <li
      v-bind="$attrs"
      :class="[
        $style['sidebar-link'],
        { [$style['active']]: isActive || isRelatedRouteActive, [$style['plain']]: plain },
      ]"
      @click="handleClick"
    >
      <a
        :aria-current="isActive || isRelatedRouteActive ? 'page' : 'false'"
        :href
        @click="navigate"
      >
        <slot />
      </a>
    </li>
  </router-link>
  <li
    v-else-if="notAllowedTitle"
    v-bind="$attrs"
    :class="$style['sidebar-link']"
    :title="`Disabled: ${notAllowedTitle}`"
  >
    <div aria-hidden="true">
      <slot />
    </div>
  </li>
</template>

<script>
import { sidebarItemClicked } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { toKebabCase } from "@/utils/text";

export default {
  name: "SidebarLink",
  inheritAttrs: false,
  props: {
    to: { type: Object, required: true },
    notAllowedTitle: { type: String, default: undefined },
    relatedRoutes: { type: Array, default: () => [] },
    plain: { type: Boolean, default: false },
  },
  computed: {
    allowed() {
      // In Vue Router 4, use resolve() instead of matcher.match()
      const route = this.$router.resolve(this.to);
      // Use the router's isRouteAllowed method attached in router/index.js
      return this.$router.isRouteAllowed(route);
    },
    isRelatedRouteActive() {
      if (this.relatedRoutes.length) {
        return this.relatedRoutes.includes(this.$route.name);
      }
      return false;
    },
  },
  methods: {
    handleClick() {
      trackEvent(sidebarItemClicked(toKebabCase(this.to.name)));
    },
  },
};
</script>

<style lang="scss" module>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.sidebar-link {
  display: block;
  padding: 0;
  margin: 0;
  width: 100%;
  border-radius: 6px;

  > * {
    display: inline-flex;
    padding: 0.5625rem 0.75rem;
    width: calc(100% - 1.5rem);
    font-weight: 500;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    color: p.$brand-70;
    align-items: center;
    gap: 0.75rem;
  }

  a {
    color: p.$brand-70;
    border-radius: 6px;
    transition: background-color 100ms ease;

    &:hover {
      background-color: p.$black-alpha-5;
      color: c.$main;
    }
  }

  &.plain > * {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
  }

  &.plain a:hover {
    background-color: transparent;
  }

  &.active a {
    color: c.$main;
    font-weight: 600;
  }
}
</style>
